import { SelectChangeEvent, 
         Select, 
         Autocomplete, 
         TextField, 
         Box,
         ListSubheader,
         MenuItem,
         Popover,
         ButtonBase,
         Chip,
         Checkbox,
         useTheme
       }                                          from "@mui/material";
import { GridRenderEditCellParams, 
         useGridApiContext 
       }                                          from "@mui/x-data-grid-pro";
import React,
       { useRef, 
         useState, 
         useEffect 
       }                                          from "react";
import ExpandMoreIcon                             from '@mui/icons-material/ExpandMore';
import { cloneDeep }                              from "lodash";
import { APIData }                                from "types/cms/frontendTypes";

export const ApplianceCategoryEditCell = (props : GridRenderEditCellParams) => {

  const { id, value, field } = props;
  const apiRef = useGridApiContext();

  const [selectedOption, setSelectedOption] = useState<string>(value || props.defaultValue);

  const handleSelectionChange = (event: SelectChangeEvent) => {
    setSelectedOption(event.target.value);
    apiRef.current.setEditCellValue({ id, field: field, value: event.target.value });
  };

  return (
    <Select
      value={selectedOption}
      sx={{ 
        border: 'none', 
        width: "100%", 
        fontSize: "14px", 
        fontWeight: 400, 
        lineHeight: "22px",
        whiteSpace: 'normal', 
        wordBreak: 'break-word',
        padding: '8px',
        borderRadius: '4px',
        color: 'inherit'
      }}
      onChange={handleSelectionChange}
      fullWidth
      native
      onClick={(e) => e.stopPropagation()}
    >
      {props.options.map((option : any) => (
        <option key={option.categoryId} value={option.categoryId}>{option.name}</option>
      ))}
    </Select>
  )
}

// Custom edit cell component for propertyTypeResidential
export const MultiChoiceEditCell = (props: GridRenderEditCellParams) => {

  const { id, value, field } = props;
  const apiRef = useGridApiContext();
  const theme = useTheme();

  // Assuming your cell value is an array of selected options
  const [selectedOptions, setSelectedOptions] = useState<string[]>(value || []);

  const availableOptions = props.options

  const handleSelectionChange = (event: any, newValue: string[]) => {
    setSelectedOptions(newValue);
    apiRef.current.setEditCellValue({ id, field: field, value: newValue });
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={availableOptions}
      value={selectedOptions}
      onChange={handleSelectionChange}
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "4px",
        padding: "4px",
        fontSize: "14px",
        fontWeight: 400,
      }}
      renderInput={( field) => (
        <TextField
          {...field}
          variant="standard"
          fullWidth
        />
      )}
    />
  );
};

// Custom edit cell component for single choice selection
export const SingleChoiceEditCell = (props: GridRenderEditCellParams) => {

  const { id, value, field } = props;
  const apiRef = useGridApiContext();
  const theme = useTheme();

  // Cell value is a single string option
  const [selectedOption, setSelectedOption] = useState<string | null>(value || null);

  const availableOptions = props.options

  const handleSelectionChange = (event: any, newValue: string | null) => {
    setSelectedOption(newValue);
    apiRef.current.setEditCellValue({ id, field: field, value: newValue });
  };

  return (
    <Autocomplete
      options={availableOptions}
      value={selectedOption}
      onChange={handleSelectionChange}
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "4px",
        padding: "4px",
        fontSize: "14px",
        fontWeight: 400,
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          fullWidth
        />
      )}
    />
  );
};

export const HomeProfileEditCell = (props: GridRenderEditCellParams) => {

  const { id, value, field } = props;
  const apiRef = useGridApiContext();
  const theme = useTheme();

  const [selectedOptions, setSelectedOptions] = useState<string[]>(value || []);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const availableOptions = props.options as { id : string, name : string, tag : string }[]

  const open = Boolean(anchorEl);
  const popoverId = open ? `home-profile-popover-${id}-${field}` : undefined;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    apiRef.current.stopCellEditMode({ id, field });
  };

  const handleOptionToggle = (optionId: string) => {
    const newSelectedOptions = selectedOptions.includes(optionId)
      ? selectedOptions.filter(id => id !== optionId)
      : [...selectedOptions, optionId];
    
    setSelectedOptions(newSelectedOptions);
    apiRef.current.setEditCellValue({ id, field: field, value: newSelectedOptions });
  };

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      <ButtonBase
        onClick={handleClick}
        sx={{
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          cursor: 'pointer',
        }}
        aria-describedby={popoverId}
      >
        <div style={{ display: 'flex', verticalAlign: 'top', height: '100%',
            flexWrap: 'wrap', gap: '4px', padding: '5px' }}>
          {selectedOptions.map((optionId) => {
            const option = availableOptions.find(opt => opt.id === optionId);
            return (
              <Chip
                key={optionId}
                label={option?.name || optionId}
                size="small"
                onDelete={() => handleOptionToggle(optionId)}
                onClick={(e) => e.stopPropagation()}
              />
            );
          })}
        </div>
        <ExpandMoreIcon fontSize="small" />
      </ButtonBase>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ minWidth: 200, maxHeight: 300, overflowY: 'auto' }}>
          {availableOptions.map((option) => (
            <MenuItem
              key={option.id}
              sx={{
                fontSize: '14px',
              }}
              onClick={() => handleOptionToggle(option.id)}
            >
              <Checkbox checked={selectedOptions.includes(option.id)} />
              {option.name}
            </MenuItem>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

export interface GroupSelectEditCellProps extends GridRenderEditCellParams {
  groupedOptions: {
    group: string;
    label: string;
    children: { value: string; label: string }[];
  }[];
}

export function GroupSingleSelectEditCell(props: GroupSelectEditCellProps) {
  
  const { id, field, value } = props;
  const apiRef = useGridApiContext();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedValue, setSelectedValue] = useState<Record<string, string>>(value);

  const open = Boolean(anchorEl);
  const popoverId = open ? `hierarchical-popover-${id}-${field}` : undefined;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    apiRef.current.stopCellEditMode({ id, field })
  };

  const handleMenuItemClick = (itemValue: { [key: string]: string }) => {
    setSelectedValue({...selectedValue, ...itemValue});
    apiRef.current.setEditCellValue({ id, field, value: {...selectedValue, ...itemValue} });
  };

  const handleChipDelete = (itemValue: { [key: string]: string }) => {
    const newValue = {...selectedValue};
    newValue[Object.keys(itemValue)[0]] = props.defaultValue;
    setSelectedValue(newValue);
    apiRef.current.setEditCellValue({ id, field, value: newValue })
  };

  const getKeyLabel = (key : string) => {
    return props.groupedOptions.find((group : any) => group.group === key)!.label;
  }

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      <ButtonBase
        onClick={handleClick}
        sx={{
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1,
          border: `1px solid ${theme.palette.divider}`, // Add some basic styling to look like an input
          borderRadius: 1,
          cursor: 'pointer',
        }}
        aria-describedby={popoverId}
      >
        <div style={{ display: 'flex', verticalAlign: 'top', height: '100%',
            flexWrap: 'wrap', gap: '4px', padding: '5px' }}>
          {
            Object.keys(selectedValue).filter((key : string) => key !== 'id' && selectedValue[key] !== props.defaultValue)
                  .map((key : string) => (
              <Chip
                key={key}
                label={`${getKeyLabel(key)} : ${selectedValue[key]}`}
                size="medium"
                onDelete={() => handleChipDelete({[key] : selectedValue[key]})}
                onClick={(e) => e.stopPropagation()}
              />
            ))
          }
        </div>
        <ExpandMoreIcon fontSize="small" />
      </ButtonBase>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
         <Box sx={{ minWidth: 200 }}>
          {props.groupedOptions.map((groupData : any) => (
            <div key={groupData.group}>
              <ListSubheader sx={{ backgroundColor: theme.palette.background.default }}>{groupData.label}</ListSubheader>
              {groupData.children.map((item : any) => (
                <MenuItem
                  sx={{
                    fontSize: '14px',
                  }}
                  key={item.value}
                  selected={selectedValue[groupData.group] === item.value}
                  onClick={() => handleMenuItemClick({[groupData.group] : item.value})}>
                  <Checkbox checked={selectedValue[groupData.group] === item.value} />
                  {item.label}
                </MenuItem>
              ))}
            </div>
          ))}
        </Box>
      </Popover>
    </Box>
  );
}

export function GroupMultiSelectEditCell(props: GroupSelectEditCellProps) {
  
  const { id, field, value } = props;
  const apiRef = useGridApiContext();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedValue, setSelectedValue] = useState<Record<string, string[]>>(value);

  const open = Boolean(anchorEl);
  const popoverId = open ? `hierarchical-popover-${id}-${field}` : undefined;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    apiRef.current.stopCellEditMode({ id, field })
  };

  const handleMenuItemClick = (key : string, value : string) => {

    const currSelectedValue = cloneDeep(selectedValue)

    if (!currSelectedValue[key]) {
      currSelectedValue[key] = [value]
    } else if (currSelectedValue[key].indexOf(value) === -1) {
      currSelectedValue[key].push(value)
    } else {
      currSelectedValue[key] = currSelectedValue[key].filter((v : string) => v !== value)
    }

    setSelectedValue(currSelectedValue);
    apiRef.current.setEditCellValue({ id, field, value: currSelectedValue });
  }

  const handleChipDelete = (key : string, value : string) => {
    const newValue = {...selectedValue};
    newValue[key] = selectedValue[key].filter((v : string) => v !== value)
    setSelectedValue(newValue);
    apiRef.current.setEditCellValue({ id, field, value: newValue })
  }

  const getKeyLabel = (key : string) => {
    return props.groupedOptions.find((group : any) => group.group === key)!.label;
  }

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      <ButtonBase
        onClick={handleClick}
        sx={{
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1,
          border: `1px solid ${theme.palette.divider}`, // Add some basic styling to look like an input
          borderRadius: 1,
          cursor: 'pointer',
        }}
        aria-describedby={popoverId}
      >
        <div style={{ display: 'flex', verticalAlign: 'top', height: '100%',
            flexWrap: 'wrap', gap: '4px', padding: '5px' }}>
          {
            Object.keys(selectedValue)
                .filter((key : string) => key !== 'id')
                .map((key : string) => 
                  selectedValue[key].map((value : string) => (
                    <Chip
                      key={`${key}-${value}`}
                      label={`${getKeyLabel(key)} : ${value}`}
                      size="medium"
                      onDelete={() => handleChipDelete(key, value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ))
                )
          }
        </div>
        <ExpandMoreIcon fontSize="small" />
      </ButtonBase>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
         <Box sx={{ minWidth: 200 }}>
          {props.groupedOptions.map((groupData : any) => (
            <div key={groupData.group}>
              <ListSubheader sx={{ backgroundColor: theme.palette.background.default }}>{groupData.label}</ListSubheader>
              {groupData.children.map((item : any) => (
                <MenuItem
                  sx={{
                    fontSize: '14px',
                  }}
                  key={item.value}
                  selected={(selectedValue[groupData.group].indexOf(item.value) > -1)}
                  onClick={() => handleMenuItemClick(groupData.group, item.value)}>
                  <Checkbox checked={selectedValue[groupData.group].indexOf(item.value) > -1} />
                  {item.label}
                </MenuItem>
              ))}
            </div>
          ))}
        </Box>
      </Popover>
    </Box>
  );
}

export interface ApplianceMultiSelectEditCellProps extends GroupSelectEditCellProps {
  groupedOptions: any[];
  appliances : APIData<'api::appliance.appliance'>[]
}

export function ApplianceMultiSelectEditCell(props: ApplianceMultiSelectEditCellProps) {
  
  const { id, field, value } = props;
  const theme = useTheme();
  
  const apiRef = useGridApiContext();

  const [groupedOptions, setGroupedOptions] = useState<any[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedValue, setSelectedValue] = useState<Record<string, string[]>>(value);

  const buttonRef = useRef<HTMLButtonElement>(null);

  const open = Boolean(anchorEl);
  const popoverId = open ? `hierarchical-popover-${id}-${field}` : undefined;

  useEffect(() => {

    const newGroupedOptions = []

    for (const option of cloneDeep(props.groupedOptions)) {
      for (const subCategory of option.subCategories) {
        const filterOnApplianceLevel = subCategory.filterOnApplianceLevel

        if (!filterOnApplianceLevel) {
          const combinedAppFilters = [{
            applianceId : subCategory.applianceFilters.map((af : any) => af.applianceId).join('|'),
            applianceName : subCategory.applianceFilters.map((af : any) => af.applianceName).join('|'),
            fuelTypes : subCategory.applianceFilters[0].fuelTypes
          }]
          subCategory.applianceFilters = combinedAppFilters
        }
      }
      newGroupedOptions.push(option)
    }

    setGroupedOptions(newGroupedOptions)
    
  }, [props.groupedOptions]);

  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.click();
    }
  }, [buttonRef]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    apiRef.current.stopCellEditMode({ id, field })
  };

  const handleMenuItemClick = (applianceId : string, value : string) => {

    const currSelectedValue = cloneDeep(selectedValue)

    const appIds = applianceId.split('|')
    for (const appId of appIds) {
      if (!currSelectedValue[appId]) {
        currSelectedValue[appId] = [value]
      } else if (currSelectedValue[appId].indexOf(value) === -1) {
        currSelectedValue[appId].push(value)
      } else {
        currSelectedValue[appId] = currSelectedValue[appId].filter((v : string) => v !== value)
      }    
    }
    
    setSelectedValue(currSelectedValue);
    apiRef.current.setEditCellValue({ id, field, value: currSelectedValue });
  }

  const checkSelectionForAppliance = (applianceId : string, value : string) => {

    const appIds = applianceId.split('|')

    for (const appId of appIds) {
      if (selectedValue[appId] && selectedValue[appId].includes(value)) {
        return true
      }
    }

    return false
  }

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      <ButtonBase
        ref={buttonRef}
        onClick={handleClick}
        sx={{
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1,
          border: `1px solid ${theme.palette.divider}`, // Add some basic styling to look like an input
          borderRadius: 1,
          cursor: 'pointer',
        }}
        aria-describedby={popoverId}
      >  
      </ButtonBase>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
         <Box sx={{ minWidth: 200 }}>
          {groupedOptions.map((groupData : any, index : number) => (
            <div key={index}>

              {groupData.subCategories.map((subCategory : any, subIndex : number) => {
                return (
                  <React.Fragment key={subIndex}>
                    <ListSubheader sx={{ backgroundColor: theme.palette.background.default, fontWeight: 'bold' }}>
                      {groupData.categoryName} {subCategory.subCategoryName !== 'default' ? ` - ${subCategory.subCategoryName}` : ''}
                    </ListSubheader>
  
                    {subCategory.applianceFilters.map((appFilter : any) => (
                      <React.Fragment key={appFilter.applianceId}>
                        { appFilter.applianceId.split('|').map((val : string, index : number) => (
                          <ListSubheader key={index}>
                            {appFilter.applianceName.split('|')[index]} ({val}) 
                          </ListSubheader>
                        ))}

                        {groupData.filterOnAppCount && (
                          <>
                          <MenuItem sx={{ fontSize: '14px' }} 
                            key={"0"}
                            selected={checkSelectionForAppliance(appFilter.applianceId, '0')}
                            onClick={() => handleMenuItemClick(appFilter.applianceId, '0')}>
                            <Checkbox checked={checkSelectionForAppliance(appFilter.applianceId, '0')} />
                            0
                          </MenuItem>
                          
                          <MenuItem sx={{ fontSize: '14px' }} 
                            key={"1"}
                            selected={checkSelectionForAppliance(appFilter.applianceId, '1')}
                            onClick={() => handleMenuItemClick(appFilter.applianceId, '1')}>
                            <Checkbox checked={checkSelectionForAppliance(appFilter.applianceId, '1')} />
                            1
                          </MenuItem>

                          <MenuItem sx={{ fontSize: '14px' }} 
                            key={"2+"}
                            selected={checkSelectionForAppliance(appFilter.applianceId, '2+')}
                            onClick={() => handleMenuItemClick(appFilter.applianceId, '2+')}>
                            <Checkbox checked={checkSelectionForAppliance(appFilter.applianceId, '2+')} />
                            2+
                          </MenuItem>
                          </>
                        )}

                        {!groupData.filterOnAppCount && appFilter.fuelTypes.map((fuelType : string) => (
                          <MenuItem
                            sx={{
                              fontSize: '14px',
                            }}
                            key={fuelType}
                            selected={checkSelectionForAppliance(appFilter.applianceId, fuelType)}
                            onClick={() => handleMenuItemClick(appFilter.applianceId, fuelType)}>
                            <Checkbox checked={checkSelectionForAppliance(appFilter.applianceId, fuelType)} />
                            {fuelType}
                          </MenuItem>
                        ))}
                        <MenuItem
                          sx={{
                            fontSize: '14px',
                          }}
                          key={"NONE"}
                          selected={checkSelectionForAppliance(appFilter.applianceId, 'NONE')}
                          onClick={() => handleMenuItemClick(appFilter.applianceId, 'NONE')}>
                          <Checkbox checked={checkSelectionForAppliance(appFilter.applianceId, 'NONE')} />
                          NONE
                        </MenuItem>
                        
                      </React.Fragment>
  
                    ))}
                  </React.Fragment>                
                )
              }
              
              )}
            </div>
          ))}
        </Box>
      </Popover>
    </Box>
  );
}

export interface GroupArrayEditCellParams extends GridRenderEditCellParams {

  groupedOptions : {
    group : string,
    label : string,
    children ?: {
      id : string,
      label : string,
    }[]
  }[]
}

export function GroupArrayEditCell(props: GroupArrayEditCellParams) {
  const { id, field, value } = props;
  const apiRef = useGridApiContext();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedValue, setSelectedValue] = useState<Record<string, string[]>>(value || {});
  const [newValues, setNewValues] = useState<Record<string, string>>({});
  const [editingValue, setEditingValue] = useState<{key: string, index: number, value: string} | null>(null);

  const open = Boolean(anchorEl);
  const popoverId = open ? `hierarchical-popover-${id}-${field}` : undefined;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    apiRef.current.stopCellEditMode({ id, field });
  };

  const handleAddValue = (key: string) => {
    if (!newValues[key]?.trim()) return;

    const currSelectedValue = cloneDeep(selectedValue);
    if (!currSelectedValue[key]) {
      currSelectedValue[key] = [newValues[key]];
    } else {
      currSelectedValue[key].push(newValues[key]);
    }

    setSelectedValue(currSelectedValue);
    setNewValues({ ...newValues, [key]: '' });
    apiRef.current.setEditCellValue({ id, field, value: currSelectedValue });
  };

  const handleSelectValue = (key: string, value: string) => {
    const currSelectedValue = cloneDeep(selectedValue);
    if (!currSelectedValue[key]) {
      currSelectedValue[key] = [value];
    } else if (!currSelectedValue[key].includes(value)) {
      currSelectedValue[key].push(value);
    } else {
      currSelectedValue[key] = currSelectedValue[key].filter(v => v !== value);
    }

    setSelectedValue(currSelectedValue);
    apiRef.current.setEditCellValue({ id, field, value: currSelectedValue });
  };

  const handleRemoveValue = (key: string, index: number) => {
    const currSelectedValue = cloneDeep(selectedValue);
    currSelectedValue[key] = currSelectedValue[key].filter((_, i) => i !== index);
    
    setSelectedValue(currSelectedValue);
    apiRef.current.setEditCellValue({ id, field, value: currSelectedValue });
  };

  const handleEditValue = (key: string, index: number, newValue: string) => {
    const currSelectedValue = cloneDeep(selectedValue);
    currSelectedValue[key][index] = newValue;
    
    setSelectedValue(currSelectedValue);
    setEditingValue(null);
    apiRef.current.setEditCellValue({ id, field, value: currSelectedValue });
  };

  const getKeyLabel = (key: string) => {
    return props.groupedOptions.find((group) => group.group === key)!.label;
  };

  const getChildrenForGroup = (key: string) => {
    const group = props.groupedOptions.find((group) => group.group === key);
    return group?.children || [];
  };

  // Get unique keys from the options array
  const groupKeys: string[] = Object.keys(value).filter((key: string) => key !== 'id');

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      <ButtonBase
        onClick={handleClick}
        sx={{
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          cursor: 'pointer',
        }}
        aria-describedby={popoverId}
      >
        <div style={{ display: 'flex', verticalAlign: 'top', height: '100%',
            flexWrap: 'wrap', gap: '4px', padding: '5px' }}>
          {Object.keys(selectedValue)
            .filter((key: string) => key !== 'id')
            .map((key: string) => 
              selectedValue[key].map((value: string, index: number) => (
                <Chip
                  key={`${key}-${index}`}
                  label={`${getKeyLabel(key)} : ${value}`}
                  size="medium"
                  onDelete={() => handleRemoveValue(key, index)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingValue({ key, index, value });
                  }}
                />
              ))
            )}
        </div>
        <ExpandMoreIcon fontSize="small" />
      </ButtonBase>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ width: 300, maxHeight: 400, overflowY: 'auto'}}>
          {groupKeys.map((groupKey) => {
            const children = getChildrenForGroup(groupKey);
            const hasPredefinedOptions = children.length > 0;
            
            return (
              <Box key={groupKey} sx={{ mb: 2 }}>
                <ListSubheader sx={{ backgroundColor: theme.palette.background.default, position: 'relative' }}>{getKeyLabel(groupKey)}</ListSubheader>
                <Box sx={{ m : 1 }}>
                  {selectedValue[groupKey]?.map((value: string, index: number) => (
                    editingValue?.key === groupKey && editingValue?.index === index ? (
                      <TextField
                        key={`edit-${index}`}
                        size="small"
                        value={editingValue.value}
                        onChange={(e) => setEditingValue({ ...editingValue, value: e.target.value })}
                        onBlur={() => handleEditValue(groupKey, index, editingValue.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleEditValue(groupKey, index, editingValue.value);
                          }
                        }}
                        autoFocus
                        fullWidth
                        sx={{ mb: 1 }}
                      />
                    ) : (
                      <Chip
                        key={`${groupKey}-${index}`}
                        label={value}
                        onDelete={() => handleRemoveValue(groupKey, index)}
                        onClick={() => setEditingValue({ key: groupKey, index, value })}
                        sx={{ m: 0.5 }}
                      />
                    )
                  ))}
                  
                  {hasPredefinedOptions ? (
                    // Show predefined options with checkboxes
                    <Box sx={{ mt: 1 }}>
                      {children.map((child) => (
                        <MenuItem
                          key={child.id}
                          sx={{ fontSize: '14px' }}
                          onClick={() => handleSelectValue(groupKey, child.id)}
                        >
                          <Checkbox 
                            checked={selectedValue[groupKey]?.includes(child.id) || false} 
                          />
                          {child.label}
                        </MenuItem>
                      ))}
                    </Box>
                  ) : (
                    // Show free text input
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Add new value"
                        value={newValues[groupKey] || ''}
                        onChange={(e) => setNewValues({ ...newValues, [groupKey]: e.target.value })}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddValue(groupKey);
                          }
                        }}
                        fullWidth
                      />
                      <ButtonBase
                        onClick={() => handleAddValue(groupKey)}
                        sx={{
                          px: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          '&:hover': { backgroundColor: theme.palette.background.default }
                        }}
                      >
                        Add
                      </ButtonBase>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Popover>
    </Box>
  );
}
